import { useEffect, useState, type FormEvent } from 'react'
import { Sparkles, Loader2, AlertCircle, CheckCircle2, ExternalLink } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { invokeWithFreshAuth } from '@/lib/edge-functions'

// "Edit with AI" for a generated SDR BC deck: the AE types a free-form prompt
// and the sdr-bc-ai-edit edge function has claude-opus-5 produce a patch over
// the deck's DECK_DEFAULTS, applied through sdr-bc-update (same URL, totals
// recomputed, protected keys enforced). See tasks/plan-sdr-bc-ai-edit.md.

const EXAMPLE_PROMPTS = [
  'Cambia el ticket promedio a $45 y recalcula las tablas',
  'Quita la slide de geography',
  'Crea una slide de riesgos y mitigación con 3 bullets y las cifras clave del caso',
  'Traduce todo el deck al inglés',
]

interface AiEditResult {
  ok: boolean
  no_changes?: boolean
  url: string
  summary: string
  patched_keys?: string[]
  removed_keys?: string[]
  recomputed_keys?: string[]
  warnings?: string[]
}

interface SdrBcAiEditDialogProps {
  open: boolean
  onClose: () => void
  onSaved?: () => void
  target: { slug: string; clientName: string } | null
}

export function SdrBcAiEditDialog({ open, onClose, onSaved, target }: SdrBcAiEditDialogProps) {
  const [prompt, setPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AiEditResult | null>(null)

  useEffect(() => {
    if (open) {
      setPrompt('')
      setError(null)
      setResult(null)
    }
  }, [open, target])

  function handleClose() {
    if (submitting) return
    onClose()
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!target || !prompt.trim()) return
    setError(null)
    setResult(null)
    setSubmitting(true)
    try {
      const { data, error: invokeErr } = await invokeWithFreshAuth<AiEditResult>('sdr-bc-ai-edit', {
        body: { slug: target.slug, prompt: prompt.trim() },
        // opus-5 reads the whole deck + writes the patch — typically 30-90s.
        timeoutMs: 180_000,
      })
      if (invokeErr) {
        const ctx = (invokeErr as { context?: Response }).context
        if (ctx && typeof ctx.json === 'function') {
          try {
            const bodyJson = await ctx.clone().json() as { error?: string } | null
            if (bodyJson?.error) throw new Error(bodyJson.error)
          } catch (parseErr) {
            if (parseErr instanceof Error && parseErr.message && parseErr.message !== 'Unexpected end of JSON input') {
              throw parseErr
            }
          }
        }
        throw invokeErr
      }
      if (!data?.ok) throw new Error('Invalid server response')
      setResult(data)
      if (!data.no_changes) onSaved?.()
    } catch (err2) {
      setError(err2 instanceof Error ? err2.message : 'Error running the AI edit')
    } finally {
      setSubmitting(false)
    }
  }

  if (!target) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose() }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" aria-hidden="true" />
            Edit with AI — {target.clientName}
            <Badge variant="outline" className="ml-1 text-xs font-mono">{target.slug}</Badge>
          </DialogTitle>
          <DialogDescription>
            Describe any change in plain language: edit content on any slide (&ldquo;en la slide 11 cambia…&rdquo;),
            remove or add slides, rewrite copy, change the deck&rsquo;s language — anything except the visual
            format, which always stays the deck&rsquo;s own. Same URL, totals recomputed automatically. It
            won&rsquo;t invent data: if something is missing, it will tell you what it needs.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder='e.g. "Cambia el GRAND TOTAL a $120M y ajusta el resto", "quita la slide de subscriptions"…'
            rows={4}
            maxLength={4000}
            disabled={submitting}
            className="text-sm"
            autoFocus
          />
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_PROMPTS.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setPrompt(ex)}
                disabled={submitting}
                className="text-[11px] px-2 py-1 rounded-full border border-border bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                {ex}
              </button>
            ))}
          </div>

          {submitting && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" aria-hidden="true" />
              <span>The AI is reading the deck and writing the edit… usually 30–90 seconds.</span>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          {result && (
            <div className={`rounded-md border p-3 text-sm space-y-2 ${
              result.no_changes
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-emerald-500/40 bg-emerald-500/5'
            }`}>
              <div className="flex items-start gap-2">
                <CheckCircle2 className={`h-4 w-4 mt-0.5 shrink-0 ${result.no_changes ? 'text-amber-500' : 'text-emerald-500'}`} aria-hidden="true" />
                <div className="flex-1 space-y-1.5">
                  <p>{result.summary}</p>
                  {!result.no_changes && (
                    <div className="flex flex-wrap gap-1">
                      {(result.patched_keys ?? []).map(k => (
                        <Badge key={k} variant="secondary" className="text-[10px] font-mono">{k}</Badge>
                      ))}
                      {(result.removed_keys ?? []).map(k => (
                        <Badge key={k} variant="destructive" className="text-[10px] font-mono">− {k}</Badge>
                      ))}
                    </div>
                  )}
                  {!result.no_changes && (result.recomputed_keys?.length ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {result.recomputed_keys!.length} totals recomputed automatically.
                    </p>
                  )}
                  {(result.warnings?.length ?? 0) > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {result.warnings!.join(' · ')}
                    </p>
                  )}
                  {!result.no_changes && (
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      Open the updated deck
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              {result && !result.no_changes ? 'Done' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={submitting || !prompt.trim()}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                  Editing…
                </>
              ) : result ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
                  Make another edit
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" aria-hidden="true" />
                  Edit with AI
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
